import React from "react";
import {
  Box,
  Stack,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
} from "@mui/material";
import moment from "moment";
import {
  leavePalette,
  statusMeta,
  shiftCorrectionMeta,
  scheduleRequestTypeMeta,
  formatShiftDateRange,
} from "./shiftCorrectionHelpers";
import { TableEmptyState } from "components/CustomComponents/EmptyState";

// Presentational history table for Shift Change. It uses the same visual system
// as AttendanceCorrectionTable, but with Original/New shift columns instead of
// punch pairs.
const headCellSx = {
  color: "#000000",
  fontWeight: 700,
  fontSize: 12,
  borderBottom: `1px solid ${leavePalette.border}`,
  whiteSpace: "nowrap",
};

const bodyCellSx = {
  borderBottom: `1px solid ${leavePalette.border}`,
  fontSize: 13,
  color: leavePalette.text,
  verticalAlign: "top",
};

const dash = "-";

const scheduleDateDisplay = (row) => {
  const start = row.date ? moment(row.date) : null;
  if (!start || !start.isValid()) return "";
  const isSwap = row.request_type === "week_off_swap";
  const end = isSwap
    ? row.swap_date ? moment(row.swap_date) : null
    : row.to_date ? moment(row.to_date) : null;
  if (isSwap && end && end.isValid()) {
    return `${start.format("DD MMM YYYY")} <-> ${end.format("DD MMM YYYY")}`;
  }
  return formatShiftDateRange(row.date, row.to_date);
};

const renderCell = (row, head) => {
  switch (head.key) {
    case "request_id":
      return (
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: leavePalette.text }}>
          {row.request_id}
        </Typography>
      );
    case "date_display": {
      const start = row.date ? moment(row.date) : null;
      const isSwap = row.request_type === "week_off_swap";
      const end = isSwap
        ? row.swap_date ? moment(row.swap_date) : null
        : row.to_date ? moment(row.to_date) : null;
      return (
        <Box sx={{ lineHeight: 1.4 }}>
          <Typography component="div" sx={{ fontSize: 13, fontWeight: 500 }}>
            {scheduleDateDisplay(row) || dash}
          </Typography>
          <Typography component="div" sx={{ fontSize: 12, color: leavePalette.subText }}>
            {start
              ? end && end.isValid() && !end.isSame(start, "day")
                ? `${start.format("ddd")} - ${end.format("ddd")}`
                : start.format("ddd")
              : ""}
          </Typography>
        </Box>
      );
    }
    case "request_type_label": {
      const meta = scheduleRequestTypeMeta(row.request_type);
      return (
        <Box
          sx={{
            display: "inline-block",
            px: 1.2,
            py: 0.4,
            borderRadius: 5,
            fontSize: 11.5,
            fontWeight: 700,
            color: meta.color,
            bgcolor: `${meta.color}1A`,
            whiteSpace: "nowrap",
          }}
        >
          {meta.label}
        </Box>
      );
    }
    case "correction_label": {
      const meta = shiftCorrectionMeta(row.correction_type);
      return (
        <Box
          sx={{
            display: "inline-block",
            px: 1.2,
            py: 0.4,
            borderRadius: 5,
            fontSize: 11.5,
            fontWeight: 600,
            color: meta.color,
            bgcolor: `${meta.color}1A`,
            whiteSpace: "nowrap",
          }}
        >
          {meta.label}
        </Box>
      );
    }
    case "original_shift":
    case "corrected_shift":
      return (
        <Typography sx={{ fontSize: 13, color: leavePalette.text, whiteSpace: "nowrap" }}>
          {row[head.key] || dash}
        </Typography>
      );
    case "reason":
      return (
        <Box
          sx={{
            maxWidth: 180,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={row.reason}
        >
          {row.reason || dash}
        </Box>
      );
    case "status": {
      const color = statusMeta(row.status, row.color_code);
      return (
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
          <Typography sx={{ fontSize: 12.5, fontWeight: 600, color }}>
            {row.status || dash}
          </Typography>
        </Stack>
      );
    }
    case "applied_display":
      return (
        <Typography sx={{ fontSize: 13, color: leavePalette.subText, whiteSpace: "nowrap" }}>
          {row.applied_on ? moment(row.applied_on).format("DD MMM YYYY, hh:mm A") : dash}
        </Typography>
      );
    default:
      return row[head.key] ?? dash;
  }
};

const ShiftCorrectionMobileCard = ({ row, headers, onClick }) => {
  const statusHeader = headers.find((head) => head.key === "status");
  const detailHeaders = headers.filter((head) => head.key !== "status");

  return (
    <Box
      onClick={onClick}
      sx={{
        p: 1.5,
        border: `1px solid ${leavePalette.border}`,
        borderRadius: 2,
        backgroundColor: "#fff",
        cursor: "pointer",
      }}
    >
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 700,
              color: leavePalette.text,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {scheduleDateDisplay(row) || dash}
          </Typography>
          {statusHeader ? renderCell(row, statusHeader) : null}
        </Stack>
        {detailHeaders.map((head) => (
          <Stack
            key={head.key}
            direction="row"
            spacing={1}
            justifyContent="space-between"
            sx={{ minWidth: 0 }}
          >
            <Typography sx={{ fontSize: 12, color: leavePalette.subText }}>
              {head.name}
            </Typography>
            <Box
              sx={{
                fontSize: 12.5,
                color: leavePalette.text,
                fontWeight: 600,
                maxWidth: "58%",
                textAlign: "right",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {renderCell(row, head)}
            </Box>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

export const ShiftCorrectionTable = ({ headers = [], rows, onRowClick }) => {
  const totalCols = headers.length || 1;
  return (
    <>
    <Box sx={{ display: { xs: "block", sm: "none" } }}>
      {rows.length === 0 ? (
        <Box
          sx={{
            py: 4,
            textAlign: "center",
            color: leavePalette.subText,
            border: `1px solid ${leavePalette.border}`,
            borderRadius: 2,
            backgroundColor: "#fff",
          }}
        >
          No schedule change records found.
        </Box>
      ) : (
        <Stack spacing={1.25}>
          {rows.map((row, idx) => (
            <ShiftCorrectionMobileCard
              key={row.id ?? idx}
              row={row}
              headers={headers}
              onClick={() => onRowClick && onRowClick(row)}
            />
          ))}
        </Stack>
      )}
    </Box>
    <TableContainer sx={{ overflowX: "auto", display: { xs: "none", sm: "block" } }}>
      <Table size="small" sx={{ minWidth: 900 }}>
        <TableHead>
          <TableRow>
            {headers.map((head) => (
              <TableCell key={head.key} sx={headCellSx}>
                {head.name}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableEmptyState
              colSpan={totalCols}
              dataLength={rows}
              title="No Schedule Change Records Found"
              subtitle="There's nothing to show here yet."
            />
          ) : (
            rows.map((row, idx) => (
              <TableRow
                key={row.id ?? idx}
                hover
                sx={{ cursor: "pointer" }}
                onClick={() => onRowClick && onRowClick(row)}
              >
                {headers.map((head) => (
                  <TableCell key={head.key} sx={bodyCellSx}>
                    {renderCell(row, head)}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
    </>
  );
};
